import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "react-query";
import Navigator from "../../../Common/layouts/Navigator/Navigator";
import QuantityInput from "../../components/QuantityInput/QuantityInput";
import {
  selectMc12Steps,
  setMc12CurrentStep,
  setSelectedAccessories,
  selectSelectedAccessories,
  selectAccessories,
  selectMc12Configuration
} from "../../store/slices/mc12";
import { getCommercializedRefs, getProductImgUrl, getProductsDescriptions } from "../../../Common/utils/api/api";
import t from "../../../Common/utils/translation/translator";
import AddIcon from "../../../Common/components/Icons/Add/AddIcon";
import ProductDataSheetLink from "../../../Common/components/Shared/ProductDataSheetLink";
import { selectLocale, updateConfiguration } from "../../../Common/store/slices/common";

const Accessories = () => {
  const dispatch = useDispatch();
  const mc12Steps = useSelector(selectMc12Steps);
  const selectedAccessories = useSelector(selectSelectedAccessories);
  const accessories = useSelector(selectAccessories);
  const locale = useSelector(selectLocale);
  const configuration = useSelector(selectMc12Configuration);
  const [descriptions, setDescriptions] = useState([]);
  const [commercializedRefs, setCommercializedRefs] = useState({});

  useEffect(() => {
    const referenceIds = selectedAccessories.map((accessory) => accessory.commercialReference);
    const fetchCommercializedRefs = async () => {
      await getCommercializedRefs(referenceIds, configuration.configData?.locale).then((commercializedRefs) => {
        // console.log("commercializedRefs", commercializedRefs);
        setCommercializedRefs({ ...commercializedRefs.commercializedProducts });
      });
    };

    fetchCommercializedRefs();
  }, [selectedAccessories]);

  useEffect(() => {
    if (selectedAccessories.length > 0) {
      return;
    }
    // init Accessories
    const accessoriesList = [...getAccessoriesByCr(accessories, "LXMMCACT0A1S100", "LXMMCAMK001S100", "VW33MAP22")];
    dispatch(setSelectedAccessories(accessoriesList));
  }, []);

  const { refetch: execGetProductsDescriptions } = useQuery(
    "GET PRODUCTS DESCRIPTIONS : it's a Post method",
    async () => {
      try {
        // console.log("selectedAccessories", selectedAccessories);
        const references = selectedAccessories?.map((accessory) => accessory.commercialReference);
        const fetchedDescriptions = await getProductsDescriptions(references);
        return fetchedDescriptions;
      } catch (error) {
        console.error("ERR", error);
      }
      return undefined;
    },
    {
      enabled: false,
      onSuccess: (fetchedDescriptions) => {
        // set  descriptions for each product
        if (!selectedAccessories) return;
        if (!descriptions) return;
        // Update the descriptions state with the fetched descriptions
        const updatedDescriptions = fetchedDescriptions.reduce((acc, desc) => {
          acc[desc.catalogue_product.id] =
            desc.catalogue_product.long_desc?.translation?.[locale] ||
            desc.catalogue_product.long_desc?.translation?.["en-GB"] ||
            acc[desc.catalogue_product.id].mediumDescription;
          return acc;
        }, {});
        setDescriptions(updatedDescriptions);
      }
    }
  );

  useEffect(() => {
    if (selectedAccessories && selectedAccessories.length > 0) {
      execGetProductsDescriptions();
    }
  }, [selectedAccessories]);

  const getAccessoriesByCr = (accessories, ...crs) => {
    const selectedAccessories = [];
    if (!accessories || crs.length === 0) return selectedAccessories;
    for (let i = 0; i < crs.length; i++) {
      const tmpAcc = accessories.find((acc) => acc.commercialReference === crs[i]);
      if (tmpAcc) selectedAccessories.push({ ...tmpAcc, quantity: 0 });
    }
    return selectedAccessories;
  };

  return (
    <div className="space-y-5 py-3">
      <span className="text-sm text-gray-400">{t("mc12.ui.step.accessories.title.amount")}</span>

      {selectedAccessories.map((acc) => {
        if (commercializedRefs[acc.commercialReference]?.isCommercialized) {
          return (
            <div
              key={acc.commercialReference}
              className="flex flex-row border-solid border-2 border-gray-200 items-center justify-between p-6"
            >
              <div className="flex flex-row items-center">
                <img src={getProductImgUrl(acc.commercialReference, 80, 80)} alt="" />
                <div className="flex flex-col px-4 text-sm">
                  <div className="text-skyBlue underline font-medium">
                    <ProductDataSheetLink eventTag="" idLabel="" reference={acc.commercialReference} />
                  </div>
                  <span>{descriptions[acc.commercialReference]}</span>
                </div>
              </div>
              <div>
                {acc.quantity === 0 ? (
                  <button
                    type="button"
                    className="rounded-sm w-fit p-[0.25rem] text-sm bg-skyBlue border-[1px] border-skyBlue  text-white hover:opacity-70"
                    onClick={() => {
                      const tmpSelectedAccessories = JSON.parse(JSON.stringify(selectedAccessories));
                      const selectedAccessory = selectedAccessories.find(
                        (accessory) => acc.commercialReference === accessory.commercialReference
                      );
                      if (selectedAccessory) {
                        const existingSelectedAccessoryIndex = tmpSelectedAccessories.findIndex(
                          (accessory) => accessory.commercialReference === selectedAccessory.commercialReference
                        );
                        if (existingSelectedAccessoryIndex !== -1) {
                          tmpSelectedAccessories[existingSelectedAccessoryIndex].quantity += 1;
                        } else {
                          tmpSelectedAccessories.push({ ...selectedAccessory, quantity: 1 });
                        }
                        dispatch(setSelectedAccessories(tmpSelectedAccessories));
                      }
                    }}
                  >
                    <AddIcon />
                  </button>
                ) : (
                  <QuantityInput
                    data-testid={`quantity-input-accessory-${acc.commercialReference}`}
                    initialValue={acc.quantity}
                    min={0}
                    max={100}
                    onValueChange={(value) => {
                      const tmpSelectedAccessories = JSON.parse(JSON.stringify(selectedAccessories));
                      const selectedAccessory = selectedAccessories.find(
                        (accessory) => acc.commercialReference === accessory.commercialReference
                      );
                      if (selectedAccessory) {
                        const existingSelectedAccessoryIndex = tmpSelectedAccessories.findIndex(
                          (accessory) => accessory.commercialReference === selectedAccessory.commercialReference
                        );
                        if (existingSelectedAccessoryIndex !== -1) {
                          tmpSelectedAccessories[existingSelectedAccessoryIndex].quantity = value;
                        } else {
                          tmpSelectedAccessories.push({ ...selectedAccessory, quantity: 1 });
                        }
                        dispatch(setSelectedAccessories(tmpSelectedAccessories));
                      }
                    }}
                  />
                )}
              </div>
            </div>
          );
        }
        return null;
      })}

      <Navigator
        continueAction={() => {
          dispatch(setMc12CurrentStep(mc12Steps.FINALIZE));
          dispatch(updateConfiguration());
        }}
      />
    </div>
  );
};

export default Accessories;
